import React from 'react';
import CalendarView from './CalendarView';
import { Loader2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { eventService } from '@/services/eventService';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';

interface CalendarDisplayProps {
  calendarKey: number;
  currentView: string;
  events: any[];
  timezone: string;
  currentNow: Date | null;
  browserTimezone: string;
  showWeekends: boolean;
  sidebarExpanded: boolean;
  isMobile: boolean;
  onDateClick: (arg: any) => void;
  onSelect: (arg: any) => void;
  onEventClick: (info: any) => void;
  toggleSidebar: () => void;
  onLoadMoreDates?: () => void;
  canLoadMore?: boolean;
  calendarRef: React.RefObject<any>;
  onRefreshEvents?: () => void;
  isLoadingEvents?: boolean;
  onDatesSet?: (dateInfo: any) => void;
  filters?: {
    students: string[];
    categories: string[];
    locations: string[];
    schools: string[];
    teachers: string[];
    showCompleted: boolean;
  };
  firstDay?: number;
  slotMinTime?: string;
  slotMaxTime?: string;
  eventTextColor?: string;
}

const CalendarDisplay: React.FC<CalendarDisplayProps> = ({
  currentView,
  events,
  timezone,
  currentNow,
  browserTimezone,
  showWeekends,
  sidebarExpanded,
  isMobile,
  onDateClick,
  onSelect,
  onEventClick,
  toggleSidebar,
  calendarRef,
  onRefreshEvents,
  isLoadingEvents,
  onDatesSet,
  filters,
  firstDay,
  slotMinTime,
  slotMaxTime,
  eventTextColor,
  onLoadMoreDates,
  canLoadMore
}) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const { token } = useAuth();

  const handleEventDrop = async (eventId: string, newStart: Date, newEnd: Date) => {
    try {
      if (!token) throw new Error('Authentication token not found');

      const startStr = `${newStart.getFullYear()}-${String(newStart.getMonth() + 1).padStart(2, '0')}-${String(newStart.getDate()).padStart(2, '0')} ${String(newStart.getHours()).padStart(2, '0')}:${String(newStart.getMinutes()).padStart(2, '0')}:00`;
      const endStr = newEnd ? `${newEnd.getFullYear()}-${String(newEnd.getMonth() + 1).padStart(2, '0')}-${String(newEnd.getDate()).padStart(2, '0')} ${String(newEnd.getHours()).padStart(2, '0')}:${String(newEnd.getMinutes()).padStart(2, '0')}:00` : startStr;

      await eventService.updateEventDateTime(token, eventId, { start: startStr, end: endStr });

      toast({ title: 'Event updated', variant: 'default' });
      onRefreshEvents?.();
    } catch (e) {
      console.error('Error dropping event:', e);
      toast({ title: 'Error', description: (e as Error).message, variant: 'destructive' });
      onRefreshEvents?.(); // Refresh to revert the optimistic update
    }
  };

  const handleEventResize = async (eventId: string, newStart: Date, newEnd: Date) => {
    try {
      if (!token) throw new Error('Authentication token not found');

      const formatDate = (date: Date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        const h = String(date.getHours()).padStart(2, '0');
        const min = String(date.getMinutes()).padStart(2, '0');
        const s = String(date.getSeconds()).padStart(2, '0');
        return `${y}-${m}-${d} ${h}:${min}:${s}`;
      };

      const start = formatDate(newStart);
      const end = formatDate(newEnd);

      await eventService.updateEventDateTime(token, eventId, { start, end });
      toast({ title: 'Event updated', variant: 'default' });
      onRefreshEvents?.();
    } catch (e) {
      console.error('Error resizing event:', e);
      toast({ title: 'Error', description: (e as Error).message, variant: 'destructive' });
      onRefreshEvents?.(); // Refresh to revert the optimistic update
    }
  };

  const filteredEvents = React.useMemo(() => {
    if (!filters) return events;
    return events.filter(event => {
      if (!filters.showCompleted && event.extendedProps?.completed) return false;
      if (filters.students.length > 0) {
        const studentIds = event.extendedProps?.studentIds || [];
        if (!studentIds.some(id => filters.students.includes(id.toString()))) return false;
      }
      if (filters.categories.length > 0) {
        const categoryId = event.extendedProps?.categoryId?.toString() || '0';
        if (!filters.categories.includes(categoryId)) return false;
      }
      if (filters.locations.length > 0) {
        const locationId = event.extendedProps?.locationId?.toString() || '0';
        if (!filters.locations.includes(locationId)) return false;
      }
      if (filters.schools.length > 0) {
        const schoolId = event.extendedProps?.laschool?.toString() || '0';
        if (!filters.schools.includes(schoolId)) return false;
      }
      if (filters.teachers.length > 0) {
        const teacherId = event.extendedProps?.teacherId?.toString() || '0';
        if (!filters.teachers.includes(teacherId)) return false;
      }
      return true;
    });
  }, [events, filters]);

  return (
    <div className="relative w-full h-full">
      {isLoadingEvents && (
        <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-50">
          <div className="flex items-center bg-white p-2 rounded-lg shadow-md">
            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
            <span>{t('common.loading')}</span>
          </div>
        </div>
      )}

      <CalendarView
        currentView={currentView}
        events={filteredEvents}
        timezone={timezone}
        browserTimezone={browserTimezone}
        currentNow={currentNow}
        showWeekends={showWeekends}
        onDateClick={onDateClick}
        onEventClick={onEventClick}
        onSelect={onSelect}
        firstDay={firstDay}
        slotMinTime={slotMinTime}
        slotMaxTime={slotMaxTime}
        eventTextColor={eventTextColor}
        calendarRef={calendarRef}
        isLoadingEvents={isLoadingEvents}
        onDatesSet={onDatesSet}
        onEventDrop={handleEventDrop}
        onEventResize={handleEventResize}
        onLoadMoreDates={onLoadMoreDates}
        canLoadMore={canLoadMore}
      />
    </div>
  );
};

export default CalendarDisplay;
